import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { Badge, OutlineCard } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-regular-svg-icons';
import { RATING_OPTIONS } from 'src/utils/reviews';
import dateFormat from 'dateformat';
import RichTextPreview from '../../GenericFieldComponents/RichText/RichTextPreview';
import { IAuthorReviewItem } from '@publishing-engine/domain/types/AuthorType';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import TextWithIcon from 'src/components/TextWithIcon';
import { faBooks, faFileLines } from '@fortawesome/pro-regular-svg-icons';
import RecommendationBadge from 'src/components/RecommendationBadge';
import { SubmissionPhase } from 'src/pages/Organization/Workspace/Submissions/index';
import { AuthorReviewHistory } from '../AuthorReviewHistory';

interface Props {
  data: IAuthorReviewItem;
  selectedReviewerId: string;
}

const getTooltipLabel = (
  review: IAuthorReviewItem,
  selectedReviewerId: string
) => {
  if (review.coReview?.status !== 'accepted' || !review.coReview?.coReviewer) {
    return 'Co-reviewer';
  }

  const { coReviewer } = review.coReview;
  const mainReviewer = review.reviewer;

  const otherPerson =
    selectedReviewerId === coReviewer.id ? mainReviewer : coReviewer;
  const otherPersonAffiliation =
    selectedReviewerId === coReviewer.id
      ? mainReviewer.affiliationDetails?.organizationName
      : coReviewer.affiliation;

  return `Co-Reviewed with ${otherPerson.firstName || ''} ${
    otherPerson.lastName || ''
  } from ${otherPersonAffiliation || 'Unknown affiliation'}`;
};

const ReviewHistoryItemCard: React.FC<Props> = ({
  data,
  selectedReviewerId
}) => {
  const {
    id,
    journal,
    submission: { title, documentType, status },
    coReview,
    firstAssessment,
    revisionAssessment,
    revisionAssessmentHistory
  } = data;

  const assessments = [
    ...(firstAssessment.date ? [firstAssessment] : []),
    ...revisionAssessmentHistory,
    ...(revisionAssessment.date ? [revisionAssessment] : [])
  ].reverse();

  const {
    submissionManuscriptId,
    recommendation,
    date
  } = assessments[0]!;

  return (
    <OutlineCard key={id}>
      <Flex
        gap={2}
        borderBottomWidth="1px"
        borderBottomColor="neutralPrimaryBorder"
        padding={4}
        flexWrap="wrap"
      >
        <Badge size="context" variant="attention">
          # {submissionManuscriptId}
        </Badge>
        <Badge width={'fit-content'} variant={'neutral'} size="context">
          <Flex gap={1} alignItems="center">
            <FontAwesomeIcon icon={faStar} />
            <Text>{RATING_OPTIONS[firstAssessment?.reviewScore ?? '']?.value || 'No Rating Yet'}</Text>
          </Flex>
        </Badge>

        <Flex gap={2} alignItems={'center'} marginStart={'auto'} flexShrink={0}>
          <Text textStyle="textContext">
            {recommendation && (
              <>
                {coReview?.status === 'accepted' ? 'Co-Reviewed' : 'Reviewed'}{' '}
                {date ? `on ${dateFormat(new Date(date!), 'dd mmm yy')}` : null}
              </>
            )}
          </Text>
          {coReview?.status === 'accepted' && (
            <Tooltip
              hasArrow
              borderRadius={4}
              placement="top"
              label={getTooltipLabel(data, selectedReviewerId)}
            >
              <Box position="relative" marginLeft={1}>
                <Badge size="context" variant="interactive">
                  <FontAwesomeIcon icon={faInfoCircle} />
                </Badge>
              </Box>
            </Tooltip>
          )}
        </Flex>
      </Flex>
      <Flex flexDirection={'column'} padding={4} gap={2}>
        <Flex gap={2} flexWrap="wrap">
          <TextWithIcon icon={faFileLines} text={documentType} />
          <TextWithIcon icon={faBooks} text={journal} />
        </Flex>
        <Flex flexDirection={'column'}>
          <Tooltip
            hasArrow
            borderRadius={4}
            placement="top"
            label={<RichTextPreview value={title} />}
          >
            <Box>
              <RichTextPreview
                value={title}
                textStyle="headingGroup"
                noOfLines={1}
              />
            </Box>
          </Tooltip>
        </Flex>
        <Flex gap={2} marginTop={2} alignItems="center" flexWrap="wrap">
          <Text textStyle={'textBody'}>Reviewer Recommendation:</Text>
          <RecommendationBadge recommendation={recommendation} />
        </Flex>
        <Flex gap={2} alignItems="center" flexWrap="wrap">
          <Text textStyle={'textBody'}>Submission Status:</Text>
          <SubmissionPhase currentState={status} />
        </Flex>
        <AuthorReviewHistory items={assessments} submissionStatus={status} />
      </Flex>
    </OutlineCard>
  );
};

export default ReviewHistoryItemCard;
